"use client";
/**
 * The demonstration's state as one short capsule, for the surfaces that have
 * room for a word and not for a frame.
 *
 * It reads the same clock the frame reads, so a row and the pane it opens can
 * never disagree about whether a link is still alive. The capsule is the only
 * place `Playable` says anything at all — everywhere else a working video
 * speaks for itself.
 */
import type { ReactElement } from "react";
import {
  CresultcardChip,
  CresultcardChipRegister,
  CresultcardChipVariant,
} from "@/components/ui/Cresultcard";
import { C_WORKOUT_VIDEO_STATE_LABEL } from "./cworkoutcard.constants";
import { useCworkoutcardVideoState } from "./CworkoutcardVideo";
import {
  CworkoutcardVideoState,
  type CworkoutcardData,
} from "./cworkoutcard.types";

/** Each state's own register. Only `Failed` is drawn as a fault. */
const STATE_VARIANT: Record<CworkoutcardVideoState, CresultcardChipVariant> = {
  [CworkoutcardVideoState.Playable]: CresultcardChipVariant.Accent,
  [CworkoutcardVideoState.Expired]: CresultcardChipVariant.Caution,
  [CworkoutcardVideoState.Unresolved]: CresultcardChipVariant.Outline,
  [CworkoutcardVideoState.Absent]: CresultcardChipVariant.Outline,
  [CworkoutcardVideoState.Failed]: CresultcardChipVariant.Negative,
};

interface CworkoutcardStateCapsuleProps {
  readonly data: CworkoutcardData;
}

export function CworkoutcardStateCapsule({
  data,
}: CworkoutcardStateCapsuleProps): ReactElement {
  const state = useCworkoutcardVideoState(data);
  return (
    <CresultcardChip
      register={CresultcardChipRegister.Status}
      variant={STATE_VARIANT[state]}
    >
      {C_WORKOUT_VIDEO_STATE_LABEL[state]}
    </CresultcardChip>
  );
}
